import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import KNAdvancedDropdown from 'components/KNAdvancedDropdown/KNAdvancedDropdown'
import KNForm from 'components/KNForm/KNForm'
import KNFormRadioGroup from 'components/KNForm/KNFormRadioGroup'
import { KNProfileIcon } from 'components/KNIcon/KNIcon'
import KNLoader from 'components/KNLoader/KNLoader'
import { CompanyType } from 'context/authentication/Company.types'
import { getSelectedCompany } from 'context/authentication/User.helpers'
import { UserContext } from 'context/authentication/UserContext'
import { analyticsEvent, analyticsPageView } from 'global/helpers/analytics'
import { processDefaultValues } from 'global/helpers/form'
import { getLanguages } from 'global/helpers/languages'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import i18n from 'i18n'
import { memo, type ReactElement, useContext, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  storeLanguage,
  updateTemperaturePreferences,
  USER_PREFERENCES_QUERY_KEY,
  userPreferencesQueryOptions,
} from 'screens/UserManager/UserManager.service'
import { MeasurementsSystem, type TemperaturePreferenceFormValues } from 'screens/UserManager/UserManager.types'
import AccountInformation from './AccountInformation'
import Notifications from './Notifications'

const ProfilePage = (): ReactElement | null => {
  const theme = useTheme()
  const { control, handleSubmit, formState, reset } = useForm<TemperaturePreferenceFormValues>({
    defaultValues: processDefaultValues({
      measurementsSystem: MeasurementsSystem.METRIC,
    }),
  })
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const company = getSelectedCompany()
  const queryClient = useQueryClient()
  const preferencesQuery = useQuery(userPreferencesQueryOptions())
  const updateTemperaturePreferencesMutation = useMutation({
    mutationFn: updateTemperaturePreferences,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USER_PREFERENCES_QUERY_KEY] }),
  })
  const storeLanguageMutation = useMutation({
    mutationFn: storeLanguage,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USER_PREFERENCES_QUERY_KEY] }),
  })

  useEffect(() => {
    analyticsPageView('polestar/profile')
  }, [])

  useEffect(() => {
    if (preferencesQuery.data && !formState.isDirty) {
      reset({
        measurementsSystem: preferencesQuery.data.measurementsSystem ?? MeasurementsSystem.METRIC,
      })
    }
  }, [preferencesQuery.data, formState.isDirty, reset])

  if (!user || !company) {
    return null
  }

  const onSubmit: SubmitHandler<TemperaturePreferenceFormValues> = async (data: TemperaturePreferenceFormValues) => {
    await updateTemperaturePreferencesMutation.mutateAsync(data)
  }

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: 'primary.main',
            }}
          >
            <Stack spacing={1} direction="row" alignItems="center">
              <KNProfileIcon fontSize="large" sx={{ mr: 1, color: 'primary.light' }} />
              <Box>
                <Typography color="#fff" variant="p3" component="p">
                  {t('screens.shared.profile.profile')}
                </Typography>
                <Typography color="#fff" variant="p4" component="p">
                  {`${user.displayName ?? user.email ?? user.uid} — ${company.displayName}`}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <AccountInformation />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <Paper
              sx={{
                padding: 2,
              }}
            >
              <Typography variant="p2" color="primary.main">
                {t('screens.shared.profile.preferences')}
              </Typography>
              <Stack direction="column" spacing={2}>
                <Box>
                  <Typography variant="p6" color="primary.main">
                    {t('screens.shared.profile.language')}
                  </Typography>
                  <KNAdvancedDropdown
                    options={getLanguages().map((language) => ({
                      label: language.label,
                      value: language.languageCode,
                    }))}
                    value={i18n.language}
                    onChange={async (newValue): Promise<void> => {
                      const newLanguage = newValue
                      await i18n.changeLanguage(newLanguage)
                      await storeLanguageMutation.mutateAsync({
                        language: i18n.language,
                      })
                      localStorage.setItem('userLanguage', newLanguage)
                      analyticsEvent('polestar_change_language', [newLanguage])
                    }}
                    sx={{ color: theme.palette.primary.main }}
                  />
                </Box>
                <Box>
                  <Typography variant="p6" color="primary.main">
                    {t('screens.shared.profile.temperature_preference')}
                  </Typography>
                  {preferencesQuery.isPending && <KNLoader />}
                  {preferencesQuery.isError && (
                    <Alert severity="error">{getRequestErrorMessage(preferencesQuery.error)}</Alert>
                  )}
                  {preferencesQuery.data && (
                    <KNForm onSubmit={handleSubmit(onSubmit)}>
                      <KNFormRadioGroup
                        name="measurementsSystem"
                        control={control}
                        options={Object.keys(MeasurementsSystem).map((temperatureUnit) => ({
                          value: temperatureUnit,
                          label: t(`screens.shared.profile.${temperatureUnit}`),
                        }))}
                      />
                      <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        size="small"
                        sx={{ marginTop: '0.5rem' }}
                        disabled={!formState.isDirty}
                        loading={formState.isSubmitting}
                      >
                        {t('general.save')}
                      </Button>
                    </KNForm>
                  )}
                </Box>
              </Stack>
            </Paper>
            {company.type == CompanyType.Carrier && <Notifications />}
          </Stack>
        </Grid>
      </Grid>
    </Container>
  )
}

export default memo(ProfilePage)
